import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { TwoFADevice } from 'app/pages/security/twostepverification/model/device.model';
import { AppState } from 'app/shared/state/app.state';
import { selectCurrent2FaInfos } from 'app/shared/state/twofactor-infos.selectors';
import { EMPTY, Subject, of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { StateStorageService } from '../../core/auth/state-storage.service';
import { AppConfiguration } from '../../core/models/app-configuration.model';
import { AccountACL } from '../../shared/constants/account-acl.constants';
import { selectCurrentUser, clientTerzePartiGrants } from 'app/shared/state/user.selectors';
import { ContactResponse } from 'app/shared/model/contacts.model';
import { ContactType } from 'app/shared/constants/contact-type.constants';
import { ContactsService } from 'app/shared/service/contacts.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { SubAccount } from 'app/shared/model/sub-account';
import { Account } from 'app/core/models/account.model';
import { ComunicationSettingService } from 'app/shared/service/comunication-setting.service';

/**
 * Angular component describe sidebar and interactions with router
 */
@Component({
  selector: 'ap-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['sidebar.scss'],
})
export class SidebarComponent implements OnInit {
  @Output() onOpenRoute = new EventEmitter<void>();
  acls = AccountACL;
  destroy$: Subject<boolean> = new Subject<boolean>();

  currentCompany?: string;
  isTwoFAStatus = false;
  isMultiutente = false;
  hasClientThirdParts = false;
  visBeta = false;
  emailContact: ContactResponse;
  currentUser: Account | null = null;

  constructor(
    public readonly appConfig: AppConfiguration,
    private readonly storageService: StateStorageService,
    private readonly contactsService: ContactsService,
    private readonly comunicationSettingService: ComunicationSettingService,
    private readonly store: Store<AppState>
  ) {
    this.emailContact = new ContactResponse(ContactType.EMAIL, null);
  }

  /**
   * Gets logged company, loads 2FA and current user information.
   * Gets third party grants for multi user client
   */
  ngOnInit(): void {
    this.currentCompany = this.storageService.getLoggedCompany();
    this.load2fa();
    this.loadInfo();

    this.store
      .select(selectCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((account: Account | null) => {
        if (account) {
          this.currentUser = account;
          this.isMultiutente = account.userType === 'pec-multiutente';
          if (this.isMultiutente) {
            // only for pec-multiutente
            this.loadGrantsClientMultiUser();
          }
        }
      });
  }

  /** Emit event when user navigate to a new sidebar page */
  openRoute(): void {
    this.onOpenRoute.emit();
  }

  /**
   * Subscribes to store change in 2FA information
   */
  load2fa(): void {
    this.store
      .select(selectCurrent2FaInfos)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: { twoFaEnabled: boolean | null; twoFaDevice: TwoFADevice | null; twoFaEnabledLoaded: boolean }) => {
        if (res.twoFaEnabledLoaded) {
          this.isTwoFAStatus = res.twoFaEnabled ? true : false;
        }
      });
  }

  /**
   * Subscribes to store change of current user, gets sales channel if user type is postmaster,
   * otherwise gets contact info
   */
  loadInfo(): void {
    this.store
      .select(selectCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((account: Account | null) => {
        if (account) {
          this.currentUser = account;
          this.isMultiutente = account.userType === 'pec-multiutente';

          if (account.userType === 'pec-postmaster') {
            // chiamare api per avere le info su postmaster
            this.getSalesChanel();
          } else {
            this.getContactInfo();
          }
        }
      });
  }

  /**
   * Subscribes to store value of third party grants
   */
  loadGrantsClientMultiUser(): void {
    this.store
      .select(clientTerzePartiGrants)
      .pipe(takeUntil(this.destroy$))
      .subscribe((subAccount: SubAccount | null) => {
        if (subAccount) {
          this.hasClientThirdParts = subAccount.clientTerzePartiGrant;
        }
      });
  }

  /**
   * Load contacts from backend
   */
  getContactInfo(): void {
    this.contactsService
      .query()
      .pipe(
        catchError((resError: HttpErrorResponse) => EMPTY),
        switchMap((stepRes: HttpResponse<ContactResponse[]>) => of(stepRes.body !== null ? stepRes.body : ([] as ContactResponse[])))
      )
      .subscribe((res: ContactResponse[]) => {
        res.forEach((element: ContactResponse) => {
          if (element.type === ContactType.EMAIL) {
            this.emailContact = element;
            if (this.emailContact.verified) {
              this.visBeta = true;
            }
          }
        });
      });
  }

  /**
   * Gets sales channel from comunication settings api.
   * Sets security communications link visible/hidden based on the sales channel
   * Requests additional contacts if chanel is not PannelloPartner or Actalis
   * and sets security communications link visible/hidden whether contact is verified
   */
  getSalesChanel() {
    this.comunicationSettingService
      .getSalesChanel()
      .pipe(takeUntil(this.destroy$))
      .subscribe((selChanel: string) => {
        if (selChanel === 'PannelloPartner' || selChanel === 'Actalis') {
          this.visBeta = false;
        } else {
          this.contactsService
            .query()
            .pipe(
              catchError((resError: HttpErrorResponse) => EMPTY),
              switchMap((stepRes: HttpResponse<ContactResponse[]>) => of(stepRes.body !== null ? stepRes.body : ([] as ContactResponse[])))
            )
            .subscribe((res: ContactResponse[]) => {
              res.forEach((element: ContactResponse) => {
                if (element.type === ContactType.EMAIL) {
                  this.emailContact = element;
                  if (this.emailContact.verified) {
                    this.visBeta = true;
                  }
                }
              });
            });
        }
      });
    return true;
  }

  /**
     * Check a specific ACL for user
     * @param acl the access control list to check
     */
  verifyAcls(acl: AccountACL): boolean {
    return !this.currentUser?.acls.some((authority: AccountACL) => [acl].includes(authority));
  }
}
